import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Login as LoginIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSession } from '../context/SessionContext';
import api from '../utils/api';

// Configure axios to send cookies with requests
api.defaults.withCredentials = true;

function Login() {
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginMessage, setLoginMessage] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const navigate = useNavigate();
  const { session, checkSession } = useSession();

  // Handle authentication status changes
  useEffect(() => {
    if (session) {
      console.log('🔐 Login component: Session status changed:', {
        authenticated: session.authenticated,
        user: session.user?.name,
        isAdmin: session.user?.isAdmin
      });
      
      if (session.authenticated) {
        if (session.user?.isAdmin) {
          console.log('🔐 Login component: Redirecting to admin');
          navigate('/admin');
        } else {
          console.log('🔐 Login component: Redirecting to unauthorized');
          navigate('/unauthorized');
        }
      } else {
        console.log('🔐 Login component: User not authenticated, showing login form');
      }
    }
  }, [session, navigate]);

  // Turnstile callback function and widget initialization
  useEffect(() => {
    // Set up global callback functions
    window.onTurnstileSuccess = (token) => {
      console.log('🔒 Turnstile verification successful');
      setTurnstileToken(token);
      setError(''); // Clear any previous errors
    };

    window.onTurnstileError = (error) => {
      console.error('🔒 Turnstile verification failed:', error);
      setTurnstileToken('');
      setError('Security verification failed. Please try again.');
    };

    window.onTurnstileExpired = () => {
      console.log('🔒 Turnstile token expired');
      setTurnstileToken('');
    };

    // Initialize Turnstile widget when component mounts
    const initializeTurnstile = () => {
      if (window.turnstile) {
        console.log('🔒 Initializing Turnstile widget');
        window.turnstile.render('#turnstile-widget', {
          sitekey: process.env.REACT_APP_TURNSTILE_SITEKEY || '0x4AAAAAAB8GhuodRZZpmwu2',
          callback: (token) => {
            console.log('🔒 Turnstile verification successful');
            setTurnstileToken(token);
            setError('');
          },
          'error-callback': (error) => {
            console.error('🔒 Turnstile verification failed:', error);
            setTurnstileToken('');
            setError('Security verification failed. Please try again.');
          },
          'expired-callback': () => {
            console.log('🔒 Turnstile token expired');
            setTurnstileToken('');
          },
          theme: 'light',
          size: 'normal'
        });
      } else {
        // If Turnstile script hasn't loaded yet, try again in 100ms
        setTimeout(initializeTurnstile, 100);
      }
    };

    // Start initialization
    initializeTurnstile();

    // Cleanup function
    return () => {
      if (window.turnstile) {
        try {
          window.turnstile.remove('#turnstile-widget');
        } catch (e) {
          console.log('Turnstile cleanup:', e.message);
        }
      }
      delete window.onTurnstileSuccess;
      delete window.onTurnstileError;
      delete window.onTurnstileExpired;
    };
  }, []);

  const handleLogin = async () => {
    if (!userInput.trim()) {
      setError('Please enter your User ID or email address');
      return;
    }

    if (!turnstileToken) {
      setError('Please complete the security verification');
      return;
    }

    setLoading(true);
    setError('');
    setLoginMessage('Connecting to server...');

    try {
      console.log('🔐 Attempting login with input:', userInput);
      
      // Add timeout for hibernation message
      const hibernationTimer = setTimeout(() => {
        setLoginMessage('Server is starting up, this may take up to a minute...');
      }, 10000); // Show hibernation message after 10 seconds
      
      // Call the simple login endpoint with userInput parameter and Turnstile token
      // Use longer timeout to handle Render service hibernation (can take 30-60s to wake up)
      const response = await api.post('/auth/login', {
        userInput: userInput,
        turnstileToken: turnstileToken
      }, {
        timeout: 90000 // 90 seconds to handle hibernation wakeup
      });
      
      clearTimeout(hibernationTimer);
      
      if (response.data.success) {
        console.log('🔐 Login successful:', response.data.user);
        
        // Store authentication data in localStorage
        localStorage.setItem('pco_api_key', response.data.apiKey);
        localStorage.setItem('pco_user_id', response.data.user.id);
        
        // Check session to update the context
        await checkSession();
        
        // Navigate to admin
        navigate('/admin');
      } else {
        setError('Login failed. Please check your User ID or email address.');
      }
    } catch (error) {
      console.error('🔐 Login error:', error);
      if (error.response?.status === 403) {
        setError('User not authorized. Please contact your administrator.');
      } else if (error.response?.status === 400) {
        setError('Please enter a valid User ID or email address.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
      setLoginMessage('');
    }
  };

  // Show loading while checking authentication
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            Logging in...
          </Typography>
          {loginMessage && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {loginMessage}
            </Typography>
          )}
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component="img"
            src="https://thechurchco-production.s3.amazonaws.com/uploads/sites/1824/2020/02/Website-Logo1.png"
            alt="Logo"
            sx={{
              height: 120,
              width: 'auto',
              maxWidth: '100%',
              filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)'
            }}
          />
        </Box>

        {/* Login Card */}
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
              Welcome Back
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
              Enter your User ID or email address to access the PCO Arrivals Billboard
            </Typography>

            {error && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                <Typography variant="body2" color="error.contrastText">
                  {error}
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                User ID or Email Address
              </Typography>
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Enter your User ID or email address"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d0e8ed',
                  borderRadius: '4px',
                  fontSize: '16px',
                  boxSizing: 'border-box',
                  backgroundColor: '#ffffff',
                  color: '#1a3040'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleLogin();
                  }
                }}
              />
            </Box>

            {/* Cloudflare Turnstile Widget */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
              <div id="turnstile-widget" />
              {!turnstileToken && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Complete the security verification above
                </Typography>
              )}
              {turnstileToken && (
                <Typography variant="caption" color="success.main" sx={{ mt: 1 }}>
                  ✓ Security verification complete
                </Typography>
              )}
            </Box>

            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<LoginIcon />}
              onClick={handleLogin}
              disabled={loading || !userInput.trim() || !turnstileToken}
              sx={{
                py: 1.5,
                mb: 3,
                fontWeight: 600,
                fontSize: '1rem',
              }}
            >
              {loading ? 'Logging in...' : 'Sign In'}
            </Button>

            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Only authorized users can access this application. 
              Contact your administrator if you need access.
            </Typography>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Need access? Please contact your administrator.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
            size="small"
          >
            Refresh Page
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

export default Login;